import dayjs from 'dayjs';
import React, { lazy, Suspense, useRef } from "react";
import { useCallback, useEffect, useState, useMemo } from "react";
import { useThemeSwitcher } from "react-css-theme-switcher";
import { Link } from "react-router-dom";
import { formatNumber } from '@progress/kendo-intl';
import { Chart, ChartSeries, ChartSeriesItem, ChartSeriesLabels, ChartXAxis, ChartXAxisItem, ChartYAxis, ChartYAxisItem } from "@progress/kendo-react-charts";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { fetchApi } from "services/api";
import { Title } from "utils/title";
import { getCurrentFiscalPeriod445 } from "utils/fiscalCalendar445";
import { useInterval, useSessionStorage } from "usehooks-ts";
import SimpleDateRangePicker from "components/SimpleDateRangePicker";
import PopupNavBar from "components/Layout/PopupNavBar";
import { IDName } from "types/idname";
import { Loader } from "@progress/kendo-react-indicators";
import { CompositeFilterDescriptor, FilterDescriptor, SortDescriptor, filterBy, orderBy } from "@progress/kendo-data-query";
import { Drawer, DrawerItem } from "@progress/kendo-react-layout";
import { openWindow } from "services/openWindow";
import { Grid, GridColumn as Column, GridSortChangeEvent, GridToolbar, GridDetailRowProps, GridCustomCellProps } from '@progress/kendo-react-grid';
import { ExcelExport, ExcelExportColumn as ExportColumn } from '@progress/kendo-react-excel-export';
import { GridLoadingPanel } from "components/GridLoadingPanel";
import { ApiLink } from "TypeGen/api-link";
import CustomerInput, { CustomerInputValue } from "components/CustomerInput";
import { SvgIcon } from "@progress/kendo-react-common";
import { arrowRotateCwIcon, caretAltUpIcon, caretAltDownIcon, downloadIcon } from '@progress/kendo-svg-icons';
import { parseColor } from "@progress/kendo-drawing";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { PersonHoverCard } from "components/PersonHoverCard";
const UsersSchedule = lazy(() => import('./UsersSchedule'));

type Team = {
  ID: number;
  Name: string;
  GoalWeeklyBookedCount: number | null;
  GoalWeeklyTotalCharges: number | null;
  GoalWeeklyTotalMargin: number | null;
}

type DashboardResponse = {
  Users: UserPerformance[];
  ShiftCategories: IDName[];
  Locations: IDName[];
  Teams: Team[];
  IndustryVerticals: IDName[];
}

type IncentiveDashboardResponse = {
  Users: IncentivePerformance[];
  ShiftCategories: IDName[];
  Locations: IDName[];
  Teams: Team[];
  IndustryVerticals: IDName[];
}

type HeatmapResponse = {
  Cards: Card[];
  Heatmap: HeatmapChart;
  VehicleTypeTotals: VehicleTypeTotal[];
}

type Card = {
  Title: string;
  FormattedValue: string;
  SubFormattedValue1: string;
  SubFormattedValue2: string;
  SubFormattedValue3: string;
  PercentageChange: number | null;
  Change: string;
}

type DetailCard = {
  Title: string;
  DateTime: string;
  TotalCharges: number;
  OrderID: number | null;
  QuoteID: number | null;
}

type DetailResponse = {
  Cards: DetailCard[];
  Title: string;
}

type UserPerformance = {
  UserID: number;
  UserName: string;
  JobTitle: string;
  LocationID: number;
  ShiftCategoryID: number;
  ShiftCategory: string;
  TeamID: number;
  Team: string;
  QuoteCount: number;
  QuotedCount: number;
  OrderCount: number;
  DispatchCount: number;
  CustomerCount: number;
  TotalOrderCharges: number;
  TotalPay: number;
  TotalQuoteCharges: number;
  BrokerPercentage: number;
  MarginPercentage: number;
  ConversionPercentage: number;
  HasGoalDefined: boolean;
  GoalWeeklyBookedCount: number | null;
  GoalWeeklyDispatchedCount: number | null;
  GoalWeeklyTotalCharges: number | null;
  GoalWeeklyTotalMargin: number | null;
  BookedCountGoalPercentage: number | null;
  DispatchedCountGoalPercentage: number | null;
  TotalChargesGoalPercentage: number | null;
  TotalMarginGoalPercentage: number | null;
  Me: boolean;
  Links: ApiLink[];
}

type IncentivePerformance = {
  UserID: number;
  UserName: string;
  JobTitle: string;
  LocationID: number;
  Location: string;
  ShiftCategoryID: number;
  TeamID: number;
  CustomerCount: number;
  OrderCount: number;
  Revenue: number;
  Margin: number;
  MarginPercentage: number;
  MonthlyTotalMarginGoal: number | null;
  MarginGoalPercentage: number | null;
  HasGoalDefined: boolean;
  Me: boolean;
  Links: ApiLink[];
}

type TeamPerformance = {
  TeamID: number;
  Team: string;
  QuoteCount: number;
  QuotedCount: number;
  OrderCount: number;
  DispatchCount: number;
  TotalOrderCharges: number;
  TotalPay: number;
  TotalQuoteCharges: number;
  BrokerPercentage: number;
  MarginPercentage: number;
  ConversionPercentage: number;
  GoalWeeklyBookedCount: number | null;
  GoalWeeklyTotalCharges: number | null;
  GoalWeeklyTotalMargin: number | null;
  BookedCountGoalPercentage: number | null;
  TotalChargesGoalPercentage: number | null;
  TotalMarginGoalPercentage: number | null;
}

type HeatmapChart = {
  Title: string;
  FormatValue: string;
  SummaryLabel: string; // Total or Average
  Values: HeatmapChartValue[];
}

type HeatmapChartValue = {
  X: number;
  Y: number;
  Value: number;
}

type VehicleTypeTotal = {
  VehicleTypeName: string;
  FormatValue: string;
}

type CustomerDetail = {
  CustomerID: number;
  CustomerName: string;
  OrderCount: number;
  QuoteCount: number;
  QuotedCount: number;
  TotalOrderCharges: number;
  TotalQuoteCharges: number;
  ConversionPercentage: number;
}

type UserDashboardDetailsResponse = {
  Customers: CustomerDetail[];
}

enum HeatmapMetric {
  OrdersBooked,
  QuotesCreated,
  OrdersDispatched,
  OrderCharges,
  QuoteCharges,
  OrderRPM,
  QuoteRPM,
  MarginPercentage,
  ConversionPercentage,
}

enum HeatmapDateType {
  Booked,
  Ship,
  Delivery,
}

const dayLabels: { [index: number]: string } = {
  1: "Mon",
  2: "Tue",
  3: "Wed",
  4: "Thu",
  5: "Fri",
  6: "Sat",
  7: "Sun",
};

const yAxisLabelContent = (e: any) => dayLabels[e.value] || "";

const xAxisLabelContent = (e: { value: number }, summaryLabel: string) => {
  if (e.value === 24) return summaryLabel;
  return `${e.value % 12 || 12}:00 ${e.value < 12 ? 'AM' : 'PM'}`;
};

const VehicleTypes = [
  { ID: 1, Name: 'Cargo Van' },
  { ID: 15, Name: 'Sprinter' },
  { ID: 2, Name: 'Small Straight' },
  { ID: 3, Name: 'Large Straight' },
  { ID: 4, Name: 'Tractor' },
];

const metrics = [
  { ID: HeatmapMetric.OrdersBooked, Name: 'Orders Booked' },
  { ID: HeatmapMetric.QuotesCreated, Name: 'Quotes Created' },
  { ID: HeatmapMetric.OrdersDispatched, Name: 'Orders Dispatched' },
  { ID: HeatmapMetric.OrderCharges, Name: 'Order Charges' },
  { ID: HeatmapMetric.QuoteCharges, Name: 'Quote Charges' },
  { ID: HeatmapMetric.OrderRPM, Name: 'Order RPM' },
  { ID: HeatmapMetric.QuoteRPM, Name: 'Quote RPM' },
  { ID: HeatmapMetric.MarginPercentage, Name: 'Margin %' },
  { ID: HeatmapMetric.ConversionPercentage, Name: 'Conversion %' }
];

const dateTypes = [
  { ID: HeatmapDateType.Booked, Name: 'Booked Date' },
  { ID: HeatmapDateType.Ship, Name: 'Ship Date' },
  { ID: HeatmapDateType.Delivery, Name: 'Delivery Date' }
];

const defaultCustomer: CustomerInputValue = {
  CustomerID: 0,
  CustomerNumber: ''
}

const UserReports = () => {
  const _export = useRef<ExcelExport | null>(null);
  const { status } = useThemeSwitcher();
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [incentiveLoading, setIncentiveLoading] = useState(false);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [userView, setUserView] = useState<'performance' | 'incentive' | 'teams' | 'schedule'>('incentive');

  const [fromDate, setFromDate] = useSessionStorage<Date>(
    "UserPerformance-FromDate",
    () => getCurrentFiscalPeriod445().start.toDate(),
    {
      serializer: (value) => value ? value.toISOString() : null,
      deserializer: (value) => value ? new Date(value) : null,
    }
  );
  const [toDate, setToDate] = useSessionStorage<Date>(
    "UserPerformance-ToDate",
    () => getCurrentFiscalPeriod445().endExclusive.subtract(1, 'day').toDate(),
    {
      serializer: (value) => value ? value.toISOString() : null,
      deserializer: (value) => value ? new Date(value) : null,
    }
  );
  const [vehicleTypeIds, setVehicleTypeIDs] = useSessionStorage<number[]>("UserPerformance-VehicleTypeIDs", []);
  const [billingCustomer, setBillingCustomer] = useSessionStorage<CustomerInputValue>("UserPerformance-BillingCustomer", defaultCustomer);
  const [userId, setUserId] = useState(0);
  const [metric, setMetric] = useState(HeatmapMetric.OrdersBooked);
  const [dateType, setDateType] = useState(HeatmapDateType.Booked);
  const [cards, setCards] = useState<Card[]>([]);
  const [users, setUsers] = useState<UserPerformance[]>([]);
  const [incentiveUsers, setIncentiveUsers] = useState<IncentivePerformance[]>([]);
  const [sylectusUsers, setSylectusUsers] = useState<IDName[]>([]);
  const [userFilter, setUserFilter] = useState<FilterDescriptor>(null);
  const [heatmap, setHeatmap] = useState<HeatmapChart>({ Title: '', FormatValue: '', SummaryLabel: 'Total', Values: [] });
  const [vehicleTypeTotals, setVehicleTypeTotals] = useState<VehicleTypeTotal[]>([]);
  const [showDetails, setShowDetails] = useState(false);
  const [details, setDetails] = useState<DetailResponse>(null);
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: 'UserName', dir: 'asc' }
  ]);
  const [teamSort, setTeamSort] = useState<SortDescriptor[]>([
    { field: 'Team', dir: 'asc' }
  ]);

  const [locations, setLocations] = useState<IDName[]>([]);
  const [locationFilter, setLocationFilter] = useState<number[]>([]);
  const [shiftCategories, setShiftCategories] = useState<IDName[]>([]);
  const [shiftCategoryFilter, setShiftCategoryFilter] = useState<number[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [teamFilter, setTeamFilter] = useState<number[]>([]);
  const [industryVerticals, setIndustryVerticals] = useState<IDName[]>([]);
  const [industryVerticalFilter, setIndustryVerticalFilter] = useState<number[]>([]);
  const [allUserFilter, setAllUserFilter] = useState(true);

  const fetchDashboard = useCallback(() => {
    if (!fromDate || !toDate) return;
    setDashboardLoading(true);
    const data = {
      UserID: userId,
      IndustryVerticalIDs: industryVerticalFilter,
      VehicleTypeIDs: vehicleTypeIds,
      BillingCustomerID: billingCustomer?.CustomerID,
      FromDate: fromDate,
      ToDate: toDate
    }
    fetchApi('/api/User/Dashboard', data, 'POST')
      .then((response: DashboardResponse) => {
        setUsers(response.Users);
        setShiftCategories(response.ShiftCategories);
        setLocations(response.Locations);
        setTeams(response.Teams);
        setIndustryVerticals(response.IndustryVerticals);
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Error fetching dashboard');
      })
      .finally(() => setDashboardLoading(false));
  }, [userId, industryVerticalFilter, vehicleTypeIds, billingCustomer?.CustomerID, fromDate, toDate]);

  const fetchIncentiveDashboard = useCallback(() => {
    if (!fromDate || !toDate) return;
    setIncentiveLoading(true);
    const data = {
      UserID: userId,
      IndustryVerticalIDs: industryVerticalFilter,
      VehicleTypeIDs: vehicleTypeIds,
      BillingCustomerID: billingCustomer?.CustomerID,
      FromDate: fromDate,
      ToDate: toDate
    };
    fetchApi('/api/User/IncentiveDashboard', data, 'POST')
      .then((response: IncentiveDashboardResponse) => {
        setIncentiveUsers(response.Users);
        setShiftCategories(response.ShiftCategories);
        setLocations(response.Locations);
        setTeams(response.Teams);
        setIndustryVerticals(response.IndustryVerticals);
      })
      .catch((e) => {
        if (!e?.status) alert('Error fetching incentive dashboard');
      })
      .finally(() => setIncentiveLoading(false));
  }, [userId, industryVerticalFilter, vehicleTypeIds, billingCustomer?.CustomerID, fromDate, toDate]);

  const fetchHeatmap = useCallback(() => {
    if (!fromDate || !toDate) return;
    setHeatmapLoading(true);
    const data = {
      UserID: userId,
      LocationIDs: locationFilter,
      ShiftCategoryIDs: shiftCategoryFilter,
      TeamIDs: teamFilter,
      IndustryVerticalIDs: industryVerticalFilter,
      VehicleTypeIDs: vehicleTypeIds,
      BillingCustomerID: billingCustomer?.CustomerID,
      Metric: metric,
      DateType: dateType,
      FromDate: fromDate,
      ToDate: toDate,
    };
    fetchApi('/api/User/Heatmap', data, 'POST')
      .then((response: HeatmapResponse) => {
        setCards(response.Cards);
        setHeatmap(response.Heatmap);
        setVehicleTypeTotals(response.VehicleTypeTotals);
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Error fetching heatmap');
      })
      .finally(() => setHeatmapLoading(false));
  }, [userId, locationFilter, shiftCategoryFilter, teamFilter, industryVerticalFilter, vehicleTypeIds, billingCustomer?.CustomerID, metric, dateType, fromDate, toDate]);

  const fetchHeatmapDetails = (hour: number, weekday: number) => {
    setShowDetails(true);
    setHeatmapLoading(true);
    const data = {
      UserID: userId,
      LocationIDs: locationFilter,
      ShiftCategoryIDs: shiftCategoryFilter,
      TeamIDs: teamFilter,
      IndustryVerticalIDs: industryVerticalFilter,
      VehicleTypeIDs: vehicleTypeIds,
      BillingCustomerID: billingCustomer?.CustomerID,
      Metric: metric,
      DateType: dateType,
      FromDate: fromDate,
      ToDate: toDate,
      Weekday: weekday,
      Hour: hour,
    };
    fetchApi('/api/User/HeatmapDetails', data, 'POST')
      .then((response: DetailResponse) => {
        setDetails(response);
      })
      .catch((e) => {
        // If not problem details
        if (!e?.status) alert('Error fetching heatmap details');
        setShowDetails(false);
      })
      .finally(() => setHeatmapLoading(false));
  };

  useEffect(() => {
    fetchApi('/api/User/SylectusUsersDropdown')
    .then((data: { Users: Array<{ ID: number, Name: string }> }) => {
      setSylectusUsers([{ ID: 0, Name: 'All Users' }, ...data.Users]);
    });
  }, [setSylectusUsers]);

  const refresh = () => {
    if (userView === 'incentive') fetchIncentiveDashboard();
    else fetchDashboard();
    fetchHeatmap();
  }

  const onGridSortChange = (event: GridSortChangeEvent) => {
    setSort(event.sort);
  };

  const onTeamGridSortChange = (event: GridSortChangeEvent) => {
    setTeamSort(event.sort);
  };

  useEffect(() => {
    if (userView === 'performance' || userView === 'teams') fetchDashboard();
  }, [userView, fetchDashboard]);

  useEffect(() => {
    fetchHeatmap();
  }, [fetchHeatmap]);

  useEffect(() => {
    if (userView === 'incentive') fetchIncentiveDashboard();
  }, [userView, fetchIncentiveDashboard]);

  useInterval(() => {
    fetchHeatmap();
  }, 15 * 60 * 1000);

  const loading = dashboardLoading || heatmapLoading;

  const usersFilter: CompositeFilterDescriptor = useMemo(() => ({
    logic: 'and',
    filters: [
      {
        field: 'LocationID',
        operator: (item: number) => locationFilter.length ? locationFilter.includes(item) : true,
        value: locationFilter.length ? locationFilter : undefined
      } as FilterDescriptor,
      {
        field: 'ShiftCategoryID',
        operator: (item: number) => shiftCategoryFilter.length ? shiftCategoryFilter.includes(item) : true,
        value: shiftCategoryFilter.length ? shiftCategoryFilter : undefined
      } as FilterDescriptor,
      {
        field: 'TeamID',
        operator: (item: number) => teamFilter.length ? teamFilter.includes(item) : true,
        value: teamFilter.length ? teamFilter : undefined
      } as FilterDescriptor,
      { field: 'HasGoalDefined', operator: 'eq', value: allUserFilter ? undefined : true } as FilterDescriptor,
    ].filter(x => x.value !== undefined)
  }), [locationFilter, shiftCategoryFilter, teamFilter, allUserFilter]);

  const teamRows = useMemo(() => {
    const filtered = filterBy(users, usersFilter);
    const weeks = fromDate && toDate ? dayjs(toDate).add(1, 'day').diff(dayjs(fromDate), 'day') / 7 : 0;
    const map = new Map<number, { row: TeamPerformance; brokerCount: number }>();
    filtered.forEach(u => {
      const teamInfo = teams.find(t => t.ID === u.TeamID);
      if (!teamInfo) return;
      if (!map.has(u.TeamID)) {
        map.set(u.TeamID, {
          row: {
            TeamID: u.TeamID,
            Team: teamInfo.Name,
            QuoteCount: 0,
            QuotedCount: 0,
            OrderCount: 0,
            DispatchCount: 0,
            TotalOrderCharges: 0,
            TotalPay: 0,
            TotalQuoteCharges: 0,
            BrokerPercentage: 0,
            MarginPercentage: 0,
            ConversionPercentage: 0,
            GoalWeeklyBookedCount: teamInfo.GoalWeeklyBookedCount,
            GoalWeeklyTotalCharges: teamInfo.GoalWeeklyTotalCharges,
            GoalWeeklyTotalMargin: teamInfo.GoalWeeklyTotalMargin,
            BookedCountGoalPercentage: null,
            TotalChargesGoalPercentage: null,
            TotalMarginGoalPercentage: null,
          },
          brokerCount: 0,
        });
      }
      const entry = map.get(u.TeamID)!;
      const row = entry.row;
      row.QuoteCount += u.QuoteCount;
      row.QuotedCount += u.QuotedCount;
      row.OrderCount += u.OrderCount;
      row.DispatchCount += u.DispatchCount;
      row.TotalOrderCharges += u.TotalOrderCharges;
      row.TotalPay += Number(u.TotalPay);
      row.TotalQuoteCharges += u.TotalQuoteCharges;
      entry.brokerCount += u.BrokerPercentage * u.OrderCount;
    });
    return Array.from(map.values()).map(({ row, brokerCount }) => {
      row.BrokerPercentage = row.OrderCount > 0 ? brokerCount / row.OrderCount : 0;
      row.MarginPercentage = row.TotalOrderCharges > 0 ? (row.TotalOrderCharges - row.TotalPay) / row.TotalOrderCharges : 0;
      row.ConversionPercentage = row.QuoteCount > 0 ? row.OrderCount / row.QuoteCount : row.OrderCount > 0 ? 1 : 0;
      const margin = row.TotalOrderCharges - row.TotalPay;
      row.BookedCountGoalPercentage = row.GoalWeeklyBookedCount ? row.OrderCount / (row.GoalWeeklyBookedCount * weeks) : null;
      row.TotalChargesGoalPercentage = row.GoalWeeklyTotalCharges ? row.TotalOrderCharges / (row.GoalWeeklyTotalCharges * weeks) : null;
      row.TotalMarginGoalPercentage = row.GoalWeeklyTotalMargin ? margin / (row.GoalWeeklyTotalMargin * weeks) : null;
      return row;
    });
  }, [users, usersFilter, teams, fromDate, toDate]);

  if (status !== 'loaded') {
    return <div>Loading...</div>;
  }

  const getHeatmapColor = (value: number, heatmapValues: HeatmapChartValue[]) => {
    const hourlyValues = heatmapValues.filter(v => v.Y !== 24).map(v => v.Value);
    const maxValue = Math.max(...hourlyValues);
    const normalizedValue = maxValue > 0 ? value / maxValue : 0;

    const baseColor = parseColor("#175985");
    const minLightnessOffset = 0.05;
    const offset = 1 - minLightnessOffset;

    const hsl = baseColor.toHSL();
    const range = 100 - hsl.l;
    const point = offset - normalizedValue;
    hsl.l += Math.min(point * range, range);

    return hsl.toCss();
  };

  const DetailComponent = (props: GridDetailRowProps) => {
    const dataItem = props.dataItem as UserPerformance;
    const [customerData, setCustomerData] = useState<CustomerDetail[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      setLoading(true);
      const data = {
        UserID: dataItem.UserID,
        IndustryVerticalIDs: industryVerticalFilter,
        VehicleTypeIDs: vehicleTypeIds,
        BillingCustomerID: billingCustomer?.CustomerID,
        FromDate: fromDate,
        ToDate: toDate
      };
      fetchApi('/api/User/UserDashboardDetails', data, 'POST')
        .then((response: UserDashboardDetailsResponse) => {
          setCustomerData(response.Customers);
          setLoading(false);
        })
        .catch((error) => {
          console.error('Error fetching customer details:', error);
          setLoading(false);
        });
    }, [dataItem.UserID]);

    if (loading) {
      return <div style={{ padding: '20px', textAlign: 'center' }}>
        <Loader type="converging-spinner" />
      </div>;
    }

    return (
      <div style={{ padding: '20px' }}>
        <h5>Customer Breakdown for {dataItem.UserName}</h5>
        <Grid data={customerData}>
          <Column field="CustomerName" title="Customer Name" />
          <Column field="TotalOrderCharges" title="Order Charges" format="{0:c2}" />
          <Column field="OrderCount" title="Booked" format="{0:n0}" />
          <Column field="QuoteCount" title="Quotes" format="{0:n0}" />
          <Column field="QuotedCount" title="Quoted" format="{0:n0}" />
          <Column field="TotalQuoteCharges" title="Quote Charges" format="{0:c2}" />
          <Column field="ConversionPercentage" title="Conversion %" format="{0:p2}" />
        </Grid>
      </div>
    );
  };

  const FooterCustomCell = (props: GridCustomCellProps) => {
    let cellContent;
    if (props.field === 'UserName') {
      cellContent = `${props.dataItem.items.length} User${props.dataItem.items.length > 1 ? 's' : ''}`;
    } else if (props.field === 'TotalOrderCharges') {
      cellContent = `${formatNumber(props.dataItem.items.reduce((sum: number, item: any) => sum + (item.TotalOrderCharges || 0), 0), 'C0')}`;
    } else if (props.field === 'OrderCount') {
      cellContent = `${props.dataItem.items.reduce((sum: number, item: any) => sum + (item.OrderCount || 0), 0)}`;
    } else if (props.field === 'DispatchCount') {
      cellContent = `${props.dataItem.items.reduce((sum: number, item: any) => sum + (item.DispatchCount || 0), 0)}`;
    } else if (props.field === 'QuotedCount') {
      cellContent = `${props.dataItem.items.reduce((sum: number, item: any) => sum + (item.QuotedCount || 0), 0)}`;
    } else if (props.field === 'QuoteCount') {
      cellContent = `${props.dataItem.items.reduce((sum: number, item: any) => sum + (item.QuoteCount || 0), 0)}`;
    } else if (props.field === 'TotalQuoteCharges') {
      cellContent = `${formatNumber(props.dataItem.items.reduce((sum: number, item: any) => sum + (item.TotalQuoteCharges || 0), 0), 'C0')}`;
    }
    return (
      <td {...props.tdProps} colSpan={1}>
        {cellContent}
      </td>
    );
  };

  return <React.Fragment>
    <PopupNavBar />
    <Title string="Performance Dashboard" />
    <nav className="navbar navbar-dark bg-dark">
      <Link to="/" className="navbar-brand">PERFORMANCE DASHBOARD {loading && <Loader type="pulsing" className="p-0" />}</Link>
      <ul className="nav navbar-nav navbar-right">
        <Button
          title="Refresh"
          icon="refresh"
          svgIcon={arrowRotateCwIcon}
          disabled={loading}
          onClick={refresh}
        />
      </ul>
    </nav>
    <Drawer
      expanded={showDetails}
      position="end"
      mode="overlay"
      animation={{ duration: 400 }}
      width={300}
      onOverlayClick={() => {
        setShowDetails(false);
        setDetails(null);
      }}
      items={details?.Cards || []}
      item={(props) => {
        return <>
          {props.index === 0 && <>
            <li className="k-drawer-item k-disabled font-weight-bold">{details?.Title}</li>
            <DrawerItem separator />
          </>}
          <li
            className="k-drawer-item justify-content-between"
            onClick={() => props.onSelect(null, props.index)}
          >
            {props.Title}
            <span className="text-success">{formatNumber(props.TotalCharges, 'C2')}</span>
          </li>
        </>
      }}
      onSelect={(e) => {
        const dataItem = details?.Cards[e.itemIndex];
        if (dataItem?.OrderID) openWindow(`/Order/${dataItem.OrderID}`);
        if (dataItem?.QuoteID) openWindow(`/Quote/Index/${dataItem.QuoteID}`);
      }}
    />
    <div className="container-fluid">
      <div className="row pt-2">
        <div className="col-md-4">
          <SimpleDateRangePicker
            inputProps={{ disabled: loading }}
            value={{ start: fromDate, end: toDate }}
            onChange={(e) => {
              setFromDate(e.start);
              setToDate(e.end);
            }}
          />
        </div>
        <div className="col-md-3">
          <MultiSelect
            placeholder="Select Vehicle Types"
            disabled={loading}
            data={VehicleTypes}
            textField="Name"
            dataItemKey="ID"
            value={VehicleTypes.filter(x => vehicleTypeIds.includes(x.ID))}
            onChange={(e) => setVehicleTypeIDs((e.value as any[]).map(x => x.ID))}
          />
        </div>
        <div className="col-md-2 input-group">
          <CustomerInput
            placeholder="Select Billing Customer"
            CustomerID={billingCustomer?.CustomerID}
            CustomerNumber={billingCustomer?.CustomerNumber}
            onChange={(customer) => setBillingCustomer(customer)}
          />
        </div>
        <div className="col-sm-3">
          <DropDownList
            disabled={loading}
            data={filterBy(sylectusUsers, userFilter)}
            filterable={true}
            onFilterChange={(e) => setUserFilter(e.filter)}
            value={sylectusUsers?.find(r => r.ID === userId)}
            textField="Name"
            dataItemKey="ID"
            style={{ width: '100%' }}
            onChange={(e) => setUserId(e.target.value.ID)}
          />
        </div>
      </div>
      {heatmap.Values.length > 0 && <div className="row mt-2">
        <div className="col-md-9">
          <Chart
            style={{ height: 600 }}
            onSeriesClick={(e) => {
              if (e.dataItem.Y === 24 || e.dataItem.Value <= 0) return;
              fetchHeatmapDetails(e.dataItem.Y, e.dataItem.X);
            }}
          >
            <ChartSeries>
              <ChartSeriesItem
                type="heatmap"
                data={heatmap.Values}
                xField="X"
                yField="Y"
                field="Value"
                color={(e: { dataItem: HeatmapChartValue }) => {
                  if (e.dataItem.Y === 24) {
                    return "#000000"; // Dark color for total/avg row
                  }
                  return getHeatmapColor(e.dataItem.Value, heatmap.Values);
                }}
                size={30}
                markers={{ type: "roundedRect", border: { width: 2 } }}
              >
                <ChartSeriesLabels format={heatmap.FormatValue} />
              </ChartSeriesItem>
            </ChartSeries>
            <ChartXAxis>
              <ChartXAxisItem labels={{ content: yAxisLabelContent }} />
            </ChartXAxis>
            <ChartYAxis>
              <ChartYAxisItem
                reverse={true}
                line={{ visible: false }}
                labels={{ content: (e: any) => xAxisLabelContent(e, heatmap.SummaryLabel) }}
              />
            </ChartYAxis>
          </Chart>
        </div>
        <div className="col-md-3">
          <DropDownList
            disabled={loading}
            data={metrics}
            value={metrics.find(r => r.ID === metric)}
            textField="Name"
            dataItemKey="ID"
            style={{ width: '100%' }}
            onChange={(e) => setMetric(e.target.value.ID)}
          />
          <DropDownList
            disabled={loading}
            data={dateTypes}
            value={dateTypes.find(r => r.ID === dateType)}
            textField="Name"
            dataItemKey="ID"
            className="mt-2"
            style={{ width: '100%' }}
            onChange={(e) => setDateType(e.target.value.ID)}
          />
          {cards.map((card, index) => {
            return <div key={index} className="col-md mt-3">
              <div className="card h-100">
                <div className="card-body">
                  <h5 className="text-muted fw-normal mt-0">{card.Title}</h5>
                  <h3 className="mt-3 mb-3">{card.FormattedValue}</h3>
                  {card.SubFormattedValue1 && <p className="mb-1">{card.SubFormattedValue1}</p>}
                  {card.SubFormattedValue2 && <p className="mb-1">{card.SubFormattedValue2}</p>}
                  {card.SubFormattedValue3 && <p className="mb-1">{card.SubFormattedValue3}</p>}
                  <p className="mb-0 text-muted">
                    {card.PercentageChange !== null && <span className={`mr-2 ${(card.PercentageChange > 0 ? "text-success" : card.PercentageChange < 0 ? "text-danger" : "")}`}>
                      <SvgIcon icon={card.PercentageChange > 0 ? caretAltUpIcon : card.PercentageChange < 0 ? caretAltDownIcon : undefined} /> {formatNumber(card.PercentageChange, 'p2')}
                    </span>}
                    <span className="text-nowrap">{card.Change}</span>
                  </p>
                </div>
              </div>
            </div>
          })}
          {vehicleTypeTotals.length > 0 && <div className="col-md mt-3">
            <div className="card h-100">
              <div className="card-body">
                <h5 className="text-muted fw-normal mt-0">By Vehicle Type</h5>
                <ul className="list-unstyled mb-0">
                  {vehicleTypeTotals.map((item, index) => (
                    <li key={index} className="d-flex justify-content-between">
                      <span>{item.VehicleTypeName}</span>
                      <span>{item.FormatValue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>}
          <div className="col-md mt-3">
            <p>LOAD1, ANC, TEST, and TRANS1 are excluded from these results unless specifically selected as the Billing Customer</p>
            <p>Available, In-Progress, Completed, and Dry Run orders are included in these results.</p>
          </div>
        </div>
      </div>}

      <h4 className="mt-2 mb-0">Location</h4>
      {locations.map((location) => <Button
        key={location.ID}
        togglable
        className="mr-1 mb-1"
        selected={locationFilter.includes(location.ID)}
        onClick={() => setLocationFilter(prev => prev.includes(location.ID) ? prev.filter(x => x !== location.ID) : [...prev, location.ID])}
      >
        {location.Name}
      </Button>)}

      <h4 className="mt-2 mb-0">KPI Category</h4>
      {shiftCategories.map((shiftCategory) => <Button
        key={shiftCategory.ID}
        togglable
        className="mr-1 mb-1"
        selected={shiftCategoryFilter.includes(shiftCategory.ID)}
        onClick={() => setShiftCategoryFilter(prev => prev.includes(shiftCategory.ID) ? prev.filter(x => x !== shiftCategory.ID) : [...prev, shiftCategory.ID])}
      >
        {shiftCategory.Name}
      </Button>)}

      <h4 className="mt-2 mb-0">Team</h4>
      {teams.map((team) => <Button
        key={team.ID}
        togglable
        className="mr-1 mb-1"
        selected={teamFilter.includes(team.ID)}
        onClick={() => setTeamFilter(prev => prev.includes(team.ID) ? prev.filter(x => x !== team.ID) : [...prev, team.ID])}
      >
        {team.Name}
      </Button>)}

      <h4 className="mt-2 mb-0">Industry Vertical</h4>
      {industryVerticals.map((industryVertical) => <Button
        key={industryVertical.ID}
        togglable
        className="mr-1 mb-1"
        selected={industryVerticalFilter.includes(industryVertical.ID)}
        onClick={() => setIndustryVerticalFilter(prev => prev.includes(industryVertical.ID) ? prev.filter(x => x !== industryVertical.ID) : [...prev, industryVertical.ID])}
      >
        {industryVertical.Name}
      </Button>)}

      {userView === 'schedule' && <Suspense>
        <UsersSchedule
          allUserFilter={allUserFilter}
          teamFilter={teamFilter}
          locationFilter={locationFilter}
          setView={setUserView}
        />
      </Suspense>}

      <ExcelExport ref={_export} fileName={`Performance Dashboard ${dayjs(fromDate).format('MM-DD-YYYY')} to ${dayjs(toDate).format('MM-DD-YYYY')}`}>
        <ExportColumn field="UserName" title="User" />
        <ExportColumn field="JobTitle" title="Job Title" />
        <ExportColumn field="Location" />
        {userView === 'incentive' && <>
          <ExportColumn field="CustomerCount" title="Customers" cellOptions={{ format: "#,##0" }} />
          <ExportColumn field="OrderCount" title="Shipments Booked" cellOptions={{ format: "#,##0" }} />
          <ExportColumn field="Revenue" title="Revenue" cellOptions={{ format: "$#,##0.00" }} />
          <ExportColumn field="Margin" title="Margin $" cellOptions={{ format: "$#,##0.00" }} />
          <ExportColumn field="MarginPercentage" title="Margin %" cellOptions={{ format: "0.00%" }} />
          <ExportColumn field="MonthlyTotalMarginGoal" title="Monthly Margin $ Goal" cellOptions={{ format: "$#,##0.00" }} />
          <ExportColumn field="MarginGoalPercentage" title="% of Margin $ Goal" cellOptions={{ format: "0.00%" }} />
        </>}
        {userView !== 'incentive' && <>
        <ExportColumn field="ShiftCategory" title="KPI Category" />
        <ExportColumn field="Team" />
        <ExportColumn field="TotalOrderCharges" title="Order Charges" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="MarginPercentage" title="Average margin per shipment" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="OrderCount" title="Shipments Booked" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="DispatchCount" title="Shipments Dispatched" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="QuotedCount" title="Total Opportunities with custom rates" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="QuoteCount" title="Total Opportunities to quote" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="CustomerCount" title="Unique Quote Customers" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="AverageOrderRPM" title="Shipment RPM" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="TotalPay" title="Capacity Cost" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="BrokerPercentage" title="% of shipments brokered" cellOptions={{ format: "0.00%" }} />
        <ExportColumn field="GoalWeeklyBookedCount" title="Weekly Booked Goal" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="GoalWeeklyDispatchedCount" title="Weekly Dispatched Goal" cellOptions={{ format: "#,##0" }} />
        <ExportColumn field="GoalWeeklyTotalCharges" title="Weekly Total Charges Goal" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="GoalWeeklyTotalMargin" title="Weekly Total Margin Goal" cellOptions={{ format: "$#,##0.00" }} />
        <ExportColumn field="BookedCountGoalPercentage" title="% of Booked Goal" cellOptions={{ format: "0%" }} />
        <ExportColumn field="DispatchedCountGoalPercentage" title="% of Dispatch Goal" cellOptions={{ format: "0%" }} />
        <ExportColumn field="TotalChargesGoalPercentage" title="% of Charges Goal" cellOptions={{ format: "0%" }} />
        <ExportColumn field="TotalMarginGoalPercentage" title="% of Margin Goal" cellOptions={{ format: "0%" }} />
        </>}
      </ExcelExport>
      {userView === 'incentive' && <>
        {incentiveLoading && <GridLoadingPanel />}
        <Grid
          sortable
          sort={sort}
          filter={usersFilter}
          autoProcessData
          onSortChange={onGridSortChange}
          data={incentiveUsers}
          className="mt-2"
          style={{ minWidth: 1140 }}
          select={{ [incentiveUsers.find(x => x.Me)?.UserID]: true }}
          dataItemKey="UserID"
        >
          <GridToolbar>
            <Button togglable selected={allUserFilter} onClick={() => setAllUserFilter(prev => !prev)}>All Users</Button>
            <ButtonGroup>
              <Button togglable onClick={() => setUserView('performance')}>Performance</Button>
              <Button togglable selected onClick={() => setUserView('incentive')}>Performance-Incentive</Button>
              <Button togglable onClick={() => setUserView('teams')}>Teams</Button>
              <Button togglable onClick={() => setUserView('schedule')}>Schedule</Button>
            </ButtonGroup>
            <Button icon="download" svgIcon={downloadIcon} onClick={() => _export.current.save(orderBy(filterBy(incentiveUsers, usersFilter), sort))}>Excel</Button>
          </GridToolbar>
          <Column field="UserName" title="User" cells={{ data: (props) => {
            const data = props.dataItem as IncentivePerformance;
            const link = data.Links.find((x) => x.Name === 'UpdateGoal');
            return <td {...props.tdProps}>
              <PersonHoverCard userId={data.UserID}>
                {link ? <Link style={{ color: "#007bff" }} to={`/UserReport/${data.UserID}`}>{data.UserName}</Link> : data.UserName}
              </PersonHoverCard>
            </td>
          }}} />
          <Column field="JobTitle" title="Job Title" />
          <Column field="Location" />
          <Column field="CustomerCount" title="Customers" format="{0:n0}" />
          <Column field="OrderCount" title="Shipments Booked" format="{0:n0}" />
          <Column field="Revenue" title="Revenue" format="{0:c2}" />
          <Column field="Margin" title="Margin $" format="{0:c2}" />
          <Column field="MarginPercentage" title="Margin %" format="{0:p2}" />
          <Column field="MonthlyTotalMarginGoal" title="Monthly Margin $ Goal" format="{0:c2}" />
          <Column field="MarginGoalPercentage" title="% of Margin $ Goal" cells={{ data: (props) => <td {...props.tdProps}>
            {props.dataItem.MarginGoalPercentage !== null ? formatNumber(props.dataItem.MarginGoalPercentage, 'p2') : <span className="text-muted">No Goal</span>}
          </td>}} />
        </Grid>
        <p className="mt-2">Based on delivery date. Only Completed and Dry Run orders are included.</p>
      </>}
      {userView === 'performance' && <>
        {dashboardLoading && <GridLoadingPanel />}
        <Grid
          sortable
          sort={sort}
          filter={usersFilter}
          groupable={{ enabled: true, footer: 'always', expandable: { enabled: true, defaultExpand: false } }}
          autoProcessData
          onSortChange={onGridSortChange}
          data={users}
          className="mt-2"
          style={{ minWidth: 1140 }}
          select={{ [users.find(x => x.Me)?.UserID]: true }}
          dataItemKey="UserID"
          cells={{
            groupFooter: FooterCustomCell
          }}
          detail={DetailComponent}
        >
          <GridToolbar>
            <Button
              togglable
              selected={allUserFilter}
              onClick={() => setAllUserFilter(prev => !prev)}
            >
              All Users
            </Button>
            <ButtonGroup>
              <Button togglable selected onClick={() => setUserView('performance')}>Performance</Button>
              <Button togglable onClick={() => setUserView('incentive')}>Performance-Incentive</Button>
              <Button togglable onClick={() => setUserView('teams')}>Teams</Button>
              <Button togglable onClick={() => setUserView('schedule')}>Schedule</Button>
            </ButtonGroup>
            <Button
              icon="download"
              svgIcon={downloadIcon}
              onClick={() => _export.current.save(orderBy(filterBy(users, usersFilter), sort))}
            >
              Excel
            </Button>
          </GridToolbar>
          <Column field="UserName" title="User" cells={{ data: (props) => {
            const data = props.dataItem as UserPerformance;
            const link = data.Links.find((x) => x.Name === 'UpdateGoal');
            return <td {...props.tdProps}>
              <PersonHoverCard userId={data.UserID}>
                {link ? <Link style={{ color: "#007bff" }} to={`/UserReport/${data.UserID}`}>{data.UserName}</Link> : data.UserName}
              </PersonHoverCard>
            </td>
          }}} />
          <Column field="JobTitle" title="Job Title" />
          <Column field="Location" />
          <Column field="ShiftCategory" title="KPI Category" />
          <Column field="Team" />
          <Column field="TotalOrderCharges" title="Order Charges" format="{0:c2}" />
          <Column field="MarginPercentage" title="Avg. Margin" format="{0:p2}" />
          <Column field="OrderCount" title="Booked" format="{0:n0}" />
          <Column field="DispatchCount" title="Dispatched" format="{0:n0}" />
          <Column field="QuotedCount" title="Quoted" format="{0:n0}" cells={{ headerCell: (props) => <HeaderThElement {...props.thProps} title="Presented to the customer">{props.children}</HeaderThElement> }} />
          <Column field="QuoteCount" title="Quotes" format="{0:n0}" cells={{ headerCell: (props) => <HeaderThElement {...props.thProps} title="For solutions to present">{props.children}</HeaderThElement> }} />
          <Column field="CustomerCount" title="Customers" format="{0:n0}" />
          <Column field="TotalQuoteCharges" title="Quote Charges" format="{0:c2}" />
          <Column field="BrokerPercentage" title="Broker %" format="{0:p2}" />
          <Column field="ConversionPercentage" title="Conversion %" format="{0:p2}" />
          <Column field="BookedCountGoalPercentage" title="Book Goal" cells={{ data: (props) => <td {...props.tdProps}
            title={props.dataItem.GoalWeeklyBookedCount ? formatNumber(props.dataItem.GoalWeeklyBookedCount, 'n0') : ''}>
            {props.dataItem.BookedCountGoalPercentage !== null ? formatNumber(props.dataItem.BookedCountGoalPercentage, 'p') : <span className="text-muted">No Goal</span>}
          </td>}} />
          <Column field="DispatchedCountGoalPercentage" title="Dispatch Goal" cells={{ data: (props) => <td {...props.tdProps}
            title={props.dataItem.GoalWeeklyDispatchedCount ? formatNumber(props.dataItem.GoalWeeklyDispatchedCount, 'n0') : ''}>
            {props.dataItem.DispatchedCountGoalPercentage !== null ? formatNumber(props.dataItem.DispatchedCountGoalPercentage, 'p') : <span className="text-muted">No Goal</span>}
          </td>}} />
          <Column field="TotalChargesGoalPercentage" title="Charges Goal" cells={{ data: (props) => <td {...props.tdProps}
          title={props.dataItem.GoalWeeklyTotalCharges ? formatNumber(props.dataItem.GoalWeeklyTotalCharges, 'c0') : ''}>
            {props.dataItem.TotalChargesGoalPercentage !== null ? formatNumber(props.dataItem.TotalChargesGoalPercentage, 'p') : <span className="text-muted">No Goal</span>}
          </td>}} />
          <Column field="TotalMarginGoalPercentage" title="Margin Goal" cells={{ data: (props) => <td {...props.tdProps}
            title={props.dataItem.GoalWeeklyTotalMargin ? formatNumber(props.dataItem.GoalWeeklyTotalMargin, 'c0') : ''}>
            {props.dataItem.TotalMarginGoalPercentage !== null ? formatNumber(props.dataItem.TotalMarginGoalPercentage, 'p') : <span className="text-muted">No Goal</span>}
          </td>}} />
        </Grid>
      </>}
      {userView === 'teams' && <>
        <Grid
          sortable
          sort={teamSort}
          onSortChange={onTeamGridSortChange}
          data={orderBy(teamRows, teamSort)}
          className="mt-2"
          style={{ minWidth: 1140 }}
        >
          <GridToolbar>
            <Button
              togglable
              selected={allUserFilter}
              onClick={() => setAllUserFilter(prev => !prev)}
            >
              All Users
            </Button>
            <ButtonGroup>
              <Button
                togglable
                onClick={() => setUserView('performance')}
              >
                Performance
              </Button>
              <Button
                togglable
                onClick={() => setUserView('incentive')}
              >
                Incentive
              </Button>
              <Button
                togglable
                selected
                onClick={() => setUserView('teams')}
              >
                Teams
              </Button>
              <Button
                togglable
                onClick={() => setUserView('schedule')}
              >
                Schedule
              </Button>
            </ButtonGroup>
          </GridToolbar>
          <Column field="Team" title="Team" />
          <Column field="TotalOrderCharges" title="Order Charges" format="{0:c2}" />
          <Column field="MarginPercentage" title="Avg. Margin" format="{0:p2}" />
          <Column field="OrderCount" title="Booked" format="{0:n0}" />
          <Column field="DispatchCount" title="Dispatched" format="{0:n0}" />
          <Column field="QuotedCount" title="Quoted" format="{0:n0}" />
          <Column field="QuoteCount" title="Quotes" format="{0:n0}" />
          <Column field="TotalQuoteCharges" title="Quote Charges" format="{0:c2}" />
          <Column field="BrokerPercentage" title="Broker %" format="{0:p2}" />
          <Column field="ConversionPercentage" title="Conversion %" format="{0:p2}" />
          <Column field="BookedCountGoalPercentage" title="Book Goal" cells={{ data: (props) => <td {...props.tdProps}
            title={props.dataItem.GoalWeeklyBookedCount ? formatNumber(props.dataItem.GoalWeeklyBookedCount, 'n0') : ''}>
            {props.dataItem.BookedCountGoalPercentage !== null ? formatNumber(props.dataItem.BookedCountGoalPercentage, 'p') : <span className="text-muted">No Goal</span>}
          </td>}} />
          <Column field="TotalChargesGoalPercentage" title="Charges Goal" cells={{ data: (props) => <td {...props.tdProps}
            title={props.dataItem.GoalWeeklyTotalCharges ? formatNumber(props.dataItem.GoalWeeklyTotalCharges, 'c0') : ''}>
            {props.dataItem.TotalChargesGoalPercentage !== null ? formatNumber(props.dataItem.TotalChargesGoalPercentage, 'p') : <span className="text-muted">No Goal</span>}
          </td>}} />
          <Column field="TotalMarginGoalPercentage" title="Margin Goal" cells={{ data: (props) => <td {...props.tdProps}
            title={props.dataItem.GoalWeeklyTotalMargin ? formatNumber(props.dataItem.GoalWeeklyTotalMargin, 'c0') : ''}>
            {props.dataItem.TotalMarginGoalPercentage !== null ? formatNumber(props.dataItem.TotalMarginGoalPercentage, 'p') : <span className="text-muted">No Goal</span>}
          </td>}} />
        </Grid>
      </>}
      <br />
    </div>
  </React.Fragment>;
}

export default UserReports;
